<template>
  <el-dialog
    :title="$t('bulkPublish')"
    width="80%"
    :visible.sync="dialogVisible"
    :destroy-on-close="true"
  >
    <div class="scroll-container">
      <el-form label-position="top">
        <el-row :gutter="30" class="form-header">
          <el-col :span="6">{{ $t('keywordText') }}</el-col>
          <el-col :span="4">{{ $t('status') }}</el-col>
          <el-col :span="10">Url</el-col>
          <el-col :span="4">{{ $t('publishDate') }}</el-col>
        </el-row>
        <el-row 
          v-for="(content, index) in contents" 
          :key="content.contentId"
          :gutter="30" 
          type="flex"
          align="middle"
          class="content-row"
        >
          <el-col :span="6">
            <span>{{ content.text }}</span>
          </el-col>
          <el-col :span="4">
            <el-tag size="small">{{ $t(content.status.toLowerCase()) }}</el-tag>
          </el-col>
          <el-col :span="10">
            <el-tooltip
              :disabled="!formModel[content.contentId].isDisabled"
              :content="$t('contentLockedTooltip')"
              raw-content
            >
            <el-input 
              v-model="formModel[content.contentId].url" 
              placeholder="https://..."
              size="small"
              :disabled="formModel[content.contentId].isDisabled"
              @paste.native="handleUrlPaste($event, index)"
            />
          </el-tooltip>
          </el-col>
          <el-col :span="4">
            <el-date-picker
              v-model="formModel[content.contentId].publishedDate"
              type="date"
              placeholder="Pick a date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              size="small"
              class="date-picker"
              :disabled="formModel[content.contentId].isDisabled"
              @paste.native="handleDatePaste($event, index)"
            />
          </el-col>
        </el-row>
      </el-form>
    </div>
    <div class="form-button">
      <el-button
        type="success"
        plain
        @click="sendForm()"
      >
        {{ $t("publishContents") }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import DynamicForm from "@/components/DynamicForm";
import DatePicker from "@/pages/CampaignManager/FormItems/DatePicker.vue";

export default {
  components: {
    DatePicker,
    DynamicForm,
  },
  props: {
    bulkPublish: Object,
    contents: Array,
    value: Boolean,
  },
  data() {
    return {
      formModel: {},
      formRef: undefined,
    };
  },
  methods: {
    sendForm() {
      this.removeEmptyRowsFromForm();
      this.$emit("bulk-publish-call", this.formModel);
      this.formModel = {};
      this.dialogVisible = false;
    },

    removeEmptyRowsFromForm() {
      this.formModel = Object.fromEntries(Object.entries(this.formModel)
      .filter(([key, value]) => value.url.trim() !== '' && value.publishedDate));
    },

    handleUrlPaste(event, startIndex) {
      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text');

      const lines = pastedText.split(/\r?\n/).filter(line => line.trim() !== '');

      if (lines.length <= 1) return;

      // Prevent the default paste event so we can handle it manually
      event.preventDefault();

      lines.forEach((line, i) => {
        const targetIndex = startIndex + i;
        
        // Don't go beyond contents list
        if (this.contents[targetIndex]) {
          const contentId = this.contents[targetIndex].contentId;
          
          // Only paste if the row is not disabled/readonly
          if (!this.formModel[contentId].isDisabled) {
            this.formModel[contentId].url = line.trim();
          }
        }
      });
    },
    
    handleDatePaste(event, startIndex) {
      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text');
      const lines = pastedText.split(/\r?\n/).filter(line => line.trim() !== '');

      if (lines.length <= 1) return;

      event.preventDefault();

      lines.forEach((line, i) => {
        const targetIndex = startIndex + i;
        if (this.contents[targetIndex]) {
          const contentId = this.contents[targetIndex].contentId;

          if (!this.formModel[contentId].isDisabled) {
            const rawDate = new Date(line.trim());
            
            if (!isNaN(rawDate.getTime())) {
              const yyyy = rawDate.getFullYear();
              // make sure we get a 2-digit month and day for the date format
              const mm = String(rawDate.getMonth() + 1).padStart(2, '0');
              const dd = String(rawDate.getDate()).padStart(2, '0');
              
              this.formModel[contentId].publishedDate = `${yyyy}-${mm}-${dd}`;
            }
          }
        }
      });
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(dialogVisible) {
        this.$emit("input", dialogVisible);
      },
    }
  },
  mounted() {
    const form = {};
    this.contents.forEach(content => {
      const isLocked = content.status && !(content.status === "CLIENT_REVIEW" || content.status === "CLIENT_FINISHED_WRITING");

      form[content.contentId] = {
        url: '',
        publishedDate: '',
        isDisabled: isLocked,
      };
    });
    this.formModel = form
  }
};
</script>

<style scoped>
.scroll-container {
  max-height: 55vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 10px;
}

.form-header {
  font-weight: bold;
  margin-bottom: 10px;
}

.content-row {
  margin-bottom: 10px;
}

.date-picker {
  width: 100%;
}

.form-button {
  display: flex;
  justify-content: end;
  margin-top: 20px;
}
</style>

<i18n>
{
  "en": {
    "bulkPublish": "Publish contents in bulk",
    "publishContents": "Publish Contents",
    "contents": "Contents",
    "keywordText": "Keyword",
    "status": "Status",
    "publishDate": "Published date",
    "draft": "Draft",
    "writing": "Being written",
    "accepted": "Text validated",
    "review": "Being proofread",
    "published": "Text published",
    "modificating": "Being modified",
    "finished_writing": "Writing completed",
    "brief_draft": "Brief in draft",
    "brief_submitted": "Brief to validate",
    "brief_validated": "Brief validated",
    "client_review": "Text to validate",
    "client_modificating": "Change requested",
    "client_finished_writing": "Writing completed",
    "contentLockedTooltip": "The current status doesn't allow this content to be set to publish."
  },
  "fr": {
    "bulkPublish": "Publication multiple",
    "publishContents": "Publier les contenus",
    "contents": "Contenus",
    "keywordText": "Mot-clé",
    "status": "Statut",
    "publishDate": "Date de publication",
    "draft": "Brouillon",
    "writing": "En cours de rédaction",
    "accepted": "Texte validé",
    "review": "En cours de relecture",
    "published": "Texte publié",
    "modificating": "En cours de modification",
    "finished_writing": "Écriture terminée",
    "brief_draft": "Brief en brouillon",
    "brief_submitted": "Brief à valider",
    "brief_validated": "Brief validé",
    "client_review": "Texte à valider",
    "client_modificating": "Demande de modification",
    "client_finished_writing": "Écriture terminée",
    "contentLockedTooltip": "Le statut actuel de ce contenu ne permet pas de le passer en publier."
  },
  "de": {
    "bulkPublish": "Inhalte in Bulk veröffentlichen",
    "publishContents": "Inhalte veröffentlichen",
    "contents": "Inhalt",
    "keywordText": "Schlüsselwort",
    "status": "Status",
    "publishDate": "Veröffentlichungsdatum",
    "draft": "Desorganisiert",
    "writing": "Laufende Redaktion",
    "accepted": "Validierter Text",
    "review": "Wird überprüft",
    "published": "Texte veröffentlicht",
    "modificating": "In Bearbeitung",
    "finished_writing": "Schreiben abgeschlossen",
    "brief_draft": "Kurz im Entwurf",
    "brief_submitted": "Kurz zu validieren",
    "brief_validated": "Kurz validiert",
    "client_review": "Zu validierender Text",
    "client_modificating": "Änderungsanforderung",
    "client_finished_writing": "Schreiben abgeschlossen",
    "contentLockedTooltip": "Der aktuelle Status dieses Inhalts erlaubt keine Änderung zum Veröffentlichen."
  }
}
</i18n>
